import type { StampPointType } from '@/components/Functional/useStampPointLabel'
import { resolveCouponUnavailableReason } from '@/components/Functional/utils/couponUsage'
import { resolveStampUnavailableLabelKey } from '@/components/Functional/utils/stampAvailability'
import { time } from '@/components/Functional/utils/util'
import { SheetName } from '@/components/Pages/spots/components/SheetName'
import { TagBadge } from '@/components/Pages/spots/components/TagBadge'
import { Button, Tabs, Typography } from '@/components/UI'
import { HtmlContent } from '@/components/UI/HtmlContent'
import type { SpotCrowded } from '@/schemas/v2/spot'
import type { SpotCouponEntryData, SpotTagData } from '@/schemas/v2/spot'
import type { StampUnavailableReason } from '@/schemas/v2/user'
import {
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLine,
  IconBrandTiktok,
  IconBrandX,
  IconExternalLink,
  IconMapPin,
  IconWorld,
} from '@tabler/icons-react'
import type { ComponentType } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import { ArPhotoButton } from '../ArPhotoButton'
import { AudioGuideButton } from '../AudioGuideButton'
import styles from './SpotDetail.module.css'
import { SpotStampOverlay } from './SpotStampOverlay'

// SNS種別 → アイコン・ラベルのマッピング
const SNS_ICON_MAP: Record<string, { Icon: ComponentType<{ size?: number }>; label: string }> = {
  facebook: { Icon: IconBrandFacebook, label: 'Facebook' },
  instagram: { Icon: IconBrandInstagram, label: 'Instagram' },
  x: { Icon: IconBrandX, label: 'X (Twitter)' },
  line: { Icon: IconBrandLine, label: 'LINE' },
  tiktok: { Icon: IconBrandTiktok, label: 'TikTok' },
}

/** http/https スキームのみ許可（defense-in-depth: スキーマ側でもフィルタ済み） */
const isSafeUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

// ─────────────────────────────────────────────
// 型定義
// ─────────────────────────────────────────────
export interface SpotDetailProps {
  /** スポット名 */
  name: string | string[]
  /** ヒーロー画像 URL（単一画像の場合。imageUrls が優先） */
  imageUrl?: string
  /** ヒーロー画像 URL 一覧（複数画像スワイプ用） */
  imageUrls?: string[]
  /** スタンプ画像 URL（右下にオーバーレイ表示） */
  stampImageUrl?: string
  /** 画像総数（ページネーションドット用） */
  imageCount?: number
  /** 現在の画像インデックス（0始まり） */
  imageIndex?: number
  /** 住所 */
  address?: string
  /** 公式サイト URL */
  url?: string
  /** Google Maps リンク用のスポット座標 */
  spotLocation?: { latitude: number; longitude: number }
  /** コース名（コースタグとして表示） */
  courseNames?: string[]
  /** タグ一覧（オレンジバッジで表示） */
  tags?: SpotTagData[]
  /** SNS リンク（APIレスポンスの officialSns 配列をそのまま渡す。配列の順に表示される） */
  socialLinks?: Array<{ type: string; url: string }>
  /** AR フォトボタンを表示するか */
  hasArPhoto?: boolean
  arPhotoActive?: boolean
  arPhotoLocked?: boolean
  onArPhotoClick?: () => void
  /** 音声ガイドボタンを表示するか */
  hasAudioGuide?: boolean
  audioGuideActive?: boolean
  audioGuideLoading?: boolean
  audioGuideLocked?: boolean
  /** 位置情報ローディング中フラグ（AR フォトボタンをスケルトン表示にする） */
  geoLoading?: boolean
  onAudioGuideClick?: () => void
  /** スタンプ獲得ボタンを表示するか */
  canGetStamp?: boolean
  onStampGet?: () => void
  /** GPS スタンプ・範囲外のとき true → ボタンを非活性にして「スポットに近づいてください」表示 */
  stampOutOfRange?: boolean
  /** GPS スタンプで位置情報許可が拒否されているとき true → ボタンを非活性にして「端末の位置情報取得設定を許可してください」表示 */
  stampGpsPermissionDenied?: boolean
  /** スタンプ押印可能期間外のとき true → ボタンを非活性にして「スタンプ獲得期間外です」表示 */
  stampOutOfPeriod?: boolean
  /**
   * スタンプ獲得済みフラグ（true: ボタン disabled・ラベルを「取得済み」にする）。
   * 1スポットに複数スタンプが紐づく場合は**全件**獲得済みのときのみ true（#2540）。
   */
  stampAcquired?: boolean
  /**
   * 1件でも獲得済みか（オーバーレイの獲得済み表示・再表示導線に使用）（#2540）。
   * 未指定時は `stampAcquired` にフォールバックする（単一スタンプでは両者が一致するため）。
   */
  anyStampAcquired?: boolean
  /**
   * サーバー判定の獲得不可理由（`UserStampData.unavailableReason`）。
   * null 以外のときボタンを非活性にして理由別の文言を表示する。
   * 曜日・祝日条件や1日1回制限などクライアント側で判定できない条件を含む。
   */
  stampUnavailableReason?: StampUnavailableReason | null
  /**
   * 購入連動ロック中（所属シートが全て未購入・かつ獲得期間内）。#3414
   * ボタンは活性のままにし、押下で購入が必要な旨の注意喚起ダイアログを出す。
   */
  purchaseLocked?: boolean
  /**
   * ロック判定に必要なシート情報が未取得。#3414
   * 判定が確定するまで獲得ボタンを押させないための一時的な非活性。
   */
  stampCheckLoading?: boolean
  /** スタンプ未取得時のオーバーレイ画像 URL（campaign.nonStampImage） */
  nonStampImageUrl?: string | null
  /** ポイントラリーキャンペーン（campaignType=2）かどうか（スタンプ画像なし獲得済み時のチェックマーク表示用） */
  isPointRally?: boolean
  /**
   * スタンプ/ポイント/両方の種別。
   * isPointRally より優先される。未指定の場合は isPointRally から導出する。
   * 'STAMP': スタンプラリー/オーバーレイ/スタンプカード
   * 'POINT': ポイントラリー
   * 'STAMP_POINT': 両方混在
   */
  stampPointType?: StampPointType
  /**
   * スタンプ形状（管理画面設定値）
   * 0: 円形（デフォルト）
   * 1: 四角
   */
  stampShape?: 0 | 1
  /** 獲得済みスタンプオーバーレイ押下時のコールバック（再表示フロー起動用） */
  onStampOverlayClick?: () => void
  /** クーポン一覧（存在する場合にクーポンタブを表示） */
  coupons?: SpotCouponEntryData[]
  /** クーポンタブを表示するか（coupons が指定されている場合は自動判定） */
  isCoupons?: boolean
  /** 詳細テキスト（HTML 文字列可） */
  description?: string
  /** 混雑情報 */
  spotCrowded?: SpotCrowded
  /** クーポン使用ボタンが押されたときのコールバック（クーポン ID を渡す） */
  onCouponUse?: (couponId: number) => void
  /** アクティブなタブインデックス（0=詳細, 1=クーポン） */
  activeTab?: number
  onTabChange?: (index: number) => void
}

// ─────────────────────────────────────────────
// コンポーネント
// ─────────────────────────────────────────────
export const SpotDetail = ({
  name,
  imageUrl,
  imageUrls,
  stampImageUrl,
  imageCount = 1,
  imageIndex = 0,
  address,
  url,
  spotLocation,
  courseNames,
  tags,
  socialLinks,
  hasArPhoto = false,
  arPhotoActive = false,
  arPhotoLocked = false,
  onArPhotoClick,
  hasAudioGuide = false,
  audioGuideActive = false,
  audioGuideLoading = false,
  audioGuideLocked = false,
  geoLoading = false,
  onAudioGuideClick,
  canGetStamp = false,
  onStampGet,
  stampOutOfRange = false,
  stampGpsPermissionDenied = false,
  stampOutOfPeriod = false,
  stampAcquired = false,
  anyStampAcquired,
  stampUnavailableReason = null,
  purchaseLocked = false,
  stampCheckLoading = false,
  nonStampImageUrl,
  stampShape = 0,
  isPointRally = false,
  stampPointType: stampPointTypeProp,
  onStampOverlayClick,
  coupons,
  isCoupons,
  onCouponUse,
  description,
  spotCrowded,
  activeTab = 0,
  onTabChange,
}: SpotDetailProps) => {
  const spotName = Array.isArray(name) ? name.join(' ') : name
  const { i18n, t } = useTranslation()
  // オーバーレイは「1件でも獲得済み」で獲得済み表示にする。
  // ボタンの「取得済み」ラベル・非活性（stampAcquired = 全件獲得済み）とは基準が異なる（#2540）。
  const isOverlayAcquired = anyStampAcquired ?? stampAcquired
  const hasCoupons = isCoupons ?? (coupons !== undefined && coupons.length > 0)
  const tabs = hasCoupons
    ? [t('common.detail', '詳細'), t('spot_detail.coupon', 'クーポン')]
    : [t('common.detail', '詳細')]

  // stampPointType が明示されていない場合は isPointRally から導出（後方互換）
  const stampPointType: StampPointType = stampPointTypeProp ?? (isPointRally ? 'POINT' : 'STAMP')

  // スタンプ獲得ボタンの文言。
  // 獲得済み > 購入連動ロック > サーバー判定の獲得不可理由 > クライアント判定（期間外・GPS）の順で優先する。
  // 購入連動ロック（#3414）はサーバー判定の SHEET_LOCKED より具体的な行動を示せるため先に出す。
  // サーバー判定は曜日・祝日や1日1回制限を含むためクライアント判定より具体的。
  const stampButtonLabel = (() => {
    if (stampAcquired) {
      return stampPointType === 'POINT'
        ? t('spot_detail.point_acquired', 'ポイント取得済み')
        : t('spot_detail.stamp_acquired', 'スタンプ取得済み')
    }
    // 購入連動ロックはサーバー判定（SHEET_LOCKED の汎用文言）より具体的なため優先する（#3414）
    if (purchaseLocked) {
      return t('spot_detail.stamp_unavailable.purchase_required', 'コース購入が必要です')
    }
    if (stampUnavailableReason !== null) {
      return t(resolveStampUnavailableLabelKey(stampUnavailableReason, stampPointType))
    }
    if (stampOutOfPeriod) {
      return stampPointType === 'POINT'
        ? t('spot_detail.point_out_of_period', 'ポイント獲得期間外です')
        : t('spot_detail.stamp_out_of_period', 'スタンプ獲得期間外です')
    }
    if (stampGpsPermissionDenied) {
      return t('spot_detail.gps_permission_denied', '端末の位置情報取得設定を許可してください')
    }
    if (stampOutOfRange) {
      return t('spot_detail.close_to_the_spot', 'スポットに近づいてください')
    }
    if (stampPointType === 'POINT') return t('spot_detail.get_a_point', 'ポイントを獲得する')
    if (stampPointType === 'STAMP_POINT') return t('spot_detail.get_stamp_or_point', '獲得する')
    return t('spot_detail.get_a_stamp', 'スタンプを獲得する')
  })()
  const mapsUrl = spotLocation
    ? `https://www.google.com/maps/search/?api=1&map_action=map&query=${spotLocation.latitude},${spotLocation.longitude}`
    : undefined

  // imageUrls が指定されている場合はそちらを優先、なければ imageUrl を配列化
  const images = imageUrls && imageUrls.length > 0 ? imageUrls : imageUrl ? [imageUrl] : []
  const totalImages = images.length || imageCount

  const [currentIndex, setCurrentIndex] = useState(imageIndex)

  // クーポンボタンのラベルを取得。
  // 活性判定は status のままとし（未認証時は couponUsage.redeemableNow が常に false になり
  // ログイン誘導導線が壊れるため）、couponUsage は非活性理由の出し分けにのみ使用する。
  const getCouponButtonLabel = (coupon: SpotCouponEntryData): string => {
    switch (coupon.status) {
      case 'available':
        return t('spot_detail.coupon_button_use')
      case 'stamp_required':
        return t('spot_detail.coupon_button_stamp_required')
      case 'redeemed': {
        // DAILY で通算上限に余裕がある場合は「本日は利用済み」（明日また使える）と表示し分ける
        const reason = resolveCouponUnavailableReason(coupon.couponUsage)
        if (reason === 'limit_reached') return t('spot_detail.coupon_button_limit_reached')
        if (reason === 'used_today') return t('spot_detail.coupon_button_used_today')
        return t('spot_detail.coupon_button_redeemed')
      }
      case 'period_out':
        return t('spot_detail.coupon_button_period_out')
      case 'out_of_range':
        return t('spot_detail.coupon_button_out_of_range')
      case 'expired':
        return t('spot_detail.coupon_button_expired')
      default:
        return t('spot_detail.coupon_button_unavailable')
    }
  }

  return (
    <div>
      {/* ── ヒーロー画像（スワイプ対応） ── */}
      <div className={styles.imageArea}>
        {images.length > 0 ? (
          <Swiper
            slidesPerView={1}
            onSlideChange={(swiper) => setCurrentIndex(swiper.activeIndex)}
            initialSlide={imageIndex}
            className={styles.swiper}
          >
            {images.map((src, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: 画像順序は固定
              <SwiperSlide key={i}>
                <img
                  src={src}
                  alt={`${spotName} ${i + 1}`}
                  className={styles.heroImage}
                  data-testid="spot-hero-image"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        ) : null}
        {stampImageUrl && !(canGetStamp || isOverlayAcquired) && (
          <SpotStampOverlay status="display" stampImageUrl={stampImageUrl} stampShape={stampShape} />
        )}
        {/* スタンプオーバーレイ（スタンプあるスポットのみ表示） */}
        {(canGetStamp || isOverlayAcquired) && (
          <SpotStampOverlay
            status={isOverlayAcquired ? 'acquired' : 'not-acquired'}
            stampImageUrl={stampImageUrl}
            nonStampImageUrl={nonStampImageUrl}
            stampShape={stampShape}
            onAcquiredClick={onStampOverlayClick}
          />
        )}
      </div>
      {totalImages > 1 && (
        <div className={styles.dots} data-testid="spot-image-dots">
          {Array.from({ length: totalImages }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: ドット順序は固定
            <span
              key={i}
              className={`${styles.dot} ${i === currentIndex ? styles.dotActive : ''}`}
              aria-current={i === currentIndex ? 'true' : undefined}
            />
          ))}
        </div>
      )}

      {/* ── 情報エリア ── */}
      <div className={styles.info}>
        <h1 className={styles.name}>{spotName}</h1>

        {/* 住所・Googleマップ・URL の各行は1つのグループにまとめ、行間の gap を発生させない */}
        {(address || mapsUrl || url) && (
          <div className={styles.linkGroup}>
            {address && (
              <div data-testid="spot-address" className={styles.linkRow}>
                <IconMapPin className={styles.linkIcon} size={24} />
                {mapsUrl ? (
                  <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={styles.linkText}>
                    {address}
                  </a>
                ) : (
                  <span className={styles.linkText}>{address}</span>
                )}
              </div>
            )}

            {!address && mapsUrl && (
              <div className={styles.linkRow}>
                <IconMapPin className={styles.linkIcon} size={24} />
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className={styles.linkText}>
                  {t('spot_detail.open_google_map', 'Googleマップで開く')}
                </a>
              </div>
            )}

            {url && (
              <div className={styles.linkRow}>
                <IconWorld className={styles.linkIcon} size={24} />
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.linkText} ${styles.linkTextWithIcon}`}
                >
                  <span>{url}</span>
                  <IconExternalLink className={styles.externalIcon} size={16} />
                </a>
              </div>
            )}
          </div>
        )}

        {courseNames && courseNames.length > 0 && (
          <div data-testid="spot-course-names" className={styles.courseRow}>
            {[...new Set(courseNames)].map((course, idx) => (
              <SheetName key={idx} name={course} />
            ))}
          </div>
        )}

        {tags && tags.length > 0 && (
          <div data-testid="spot-tags" className={styles.tagRow}>
            {tags.map((tag) => (
              <TagBadge key={tag.id} tag={tag.name} />
            ))}
          </div>
        )}
      </div>

      {/* ── アクションエリア ── */}
      <div className={styles.actionArea}>
        {/* SNS アイコン（officialSns 配列の順で表示） */}
        <div className={styles.socialRow}>
          {socialLinks?.map(({ type, url }) => {
            const sns = SNS_ICON_MAP[type]
            if (!sns || !isSafeUrl(url)) return null
            const { Icon, label } = sns
            return (
              <a
                key={type}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.socialLink}
                aria-label={label}
              >
                <Icon size={32} />
              </a>
            )
          })}
        </div>

        {/* AR フォト・音声ガイドボタン */}
        {(hasArPhoto || hasAudioGuide) && (
          <div className={styles.rightActions}>
            {hasArPhoto && (
              <ArPhotoButton
                active={arPhotoActive}
                locked={arPhotoLocked}
                loading={geoLoading}
                onClick={onArPhotoClick}
              />
            )}
            {hasAudioGuide && (
              <AudioGuideButton
                active={audioGuideActive}
                locked={audioGuideLocked}
                loading={audioGuideLoading}
                onClick={onAudioGuideClick}
              />
            )}
          </div>
        )}
      </div>

      {/* ── スタンプ獲得ボタン ── */}
      {(canGetStamp || isOverlayAcquired) && (
        <div className={styles.stampButton}>
          <Button
            label={stampButtonLabel}
            variant="primary"
            fullWidth
            disabled={
              // 獲得済み・シート情報未取得は常に非活性。それ以外の獲得不可理由は、購入連動ロック中のみ
              // 注意喚起ダイアログを出すためにボタンを活性のままにする（#3414）
              stampAcquired ||
              stampCheckLoading ||
              (!purchaseLocked &&
                (stampUnavailableReason !== null || stampOutOfPeriod || stampGpsPermissionDenied || stampOutOfRange))
            }
            onClick={onStampGet}
          />
        </div>
      )}

      {/* ── タブ（2つ以上のときのみ）／1つのときは見出し「詳細」 ── */}
      {tabs.length >= 2 ? (
        <Tabs tabs={tabs} activeIndex={activeTab} onChange={onTabChange} className={styles.tabs} />
      ) : (
        <Typography variant="h5" component="h2" className={styles.detailHeading}>
          {t('common.detail', '詳細')}
        </Typography>
      )}

      {/* ── 詳細テキスト ── */}
      {(activeTab === 0 || tabs.length === 1) && description && (
        <HtmlContent html={description} variant="body3" className={styles.description} />
      )}

      {/* ── クーポンタブ ── */}
      {activeTab === 1 && coupons && coupons.length > 0 && (
        <div className={styles.couponList}>
          {coupons.map((coupon) => (
            <div key={coupon.id} className={styles.couponCard} data-testid={`coupon-card-${coupon.id}`}>
              <div className={styles.couponCardInner}>
                {coupon.image && <img src={coupon.image} alt={coupon.name ?? ''} className={styles.couponImage} />}
                <div className={styles.couponInfo}>
                  <p className={styles.couponName}>{coupon.name}</p>
                  {coupon.startAt || coupon.endAt ? (
                    <p className={styles.couponPeriod}>
                      {t('spot_detail.usage_period_range', '使用期間：{{start}} 〜 {{end}}', {
                        start: coupon.startAt ? (time({ time: coupon.startAt, language: i18n.language }) ?? '') : '',
                        end: coupon.endAt ? (time({ time: coupon.endAt, language: i18n.language }) ?? '') : '',
                      })}
                    </p>
                  ) : coupon.validDays != null ? (
                    <p className={styles.couponPeriod}>
                      {t('spot_detail.coupon_valid_days_info', { days: coupon.validDays })}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className={styles.couponButtonWrap}>
                <div
                  data-testid={
                    coupon.status === 'available' ? 'coupon-use-button' : `coupon-status-label-${coupon.status}`
                  }
                >
                  <Button
                    variant="primary"
                    fullWidth
                    disabled={coupon.status !== 'available'}
                    onClick={() => onCouponUse?.(coupon.id)}
                    label={getCouponButtonLabel(coupon)}
                  />
                </div>
              </div>
              {coupon.description && <p className={styles.couponDescription}>{coupon.description}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
